/**
 * Support Mode Context Provider
 *
 * Provides global state management for admin support mode functionality.
 * When an admin selects a tenant for support purposes, this context tracks
 * the support mode state across all pages.
 *
 * IMPORTANT: This is a shared module that should be used by ALL UI interfaces
 * (except admin) to maintain consistent support mode state.
 *
 * Features:
 * - Global support mode state accessible from any component
 * - LocalStorage persistence for state survival across page refreshes
 * - Cross-tab synchronization via storage events
 * - Clean API for entering and exiting support mode
 *
 * @fileoverview React Context for admin support mode state management
 *
 * @example
 * // Wrap your app with SupportModeProvider
 * <SupportModeProvider>
 *   <App />
 * </SupportModeProvider>
 *
 * // Access support mode state in components
 * const { isInSupportMode, supportTenant, exitSupportMode } = useSupportMode();
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import {
  SupportTenant,
  SupportModeContextType,
  SupportModeProviderProps,
  SUPPORT_MODE_STORAGE_KEY,
} from './types';

// ============================================================================
// Default Context Value
// ============================================================================

/**
 * Default context value for initialization
 */
const defaultSupportModeContext: SupportModeContextType = {
  isInSupportMode: false,
  supportTenant: null,
  enterSupportMode: () => {},
  exitSupportMode: () => {},
};

// ============================================================================
// Context Creation
// ============================================================================

/**
 * Support Mode Context instance
 */
const SupportModeContext = createContext<SupportModeContextType>(defaultSupportModeContext);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Read support mode state from localStorage
 * @returns Stored tenant or null
 */
const readFromStorage = (): SupportTenant | null => {
  try {
    const stored = localStorage.getItem(SUPPORT_MODE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.supportTenant || null;
    }
  } catch (err) {
    console.error('[SupportModeContext] Failed to parse stored state:', err);
  }
  return null;
};

/**
 * Write support mode state to localStorage
 * @param tenant - Tenant to store or null to clear
 */
const writeToStorage = (tenant: SupportTenant | null): void => {
  try {
    if (tenant) {
      localStorage.setItem(SUPPORT_MODE_STORAGE_KEY, JSON.stringify({ supportTenant: tenant }));
    } else {
      localStorage.removeItem(SUPPORT_MODE_STORAGE_KEY);
    }
  } catch (err) {
    console.error('[SupportModeContext] Failed to persist state:', err);
  }
};

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Support Mode Provider Component
 *
 * Provides support mode state to all child components via React Context.
 * Persists state to localStorage so support mode survives page refresh.
 * Syncs state across browser tabs via storage events.
 *
 * @param props - Component props
 * @returns Provider component wrapping children
 *
 * @example
 * <SupportModeProvider>
 *   <AppRouter />
 * </SupportModeProvider>
 */
export const SupportModeProvider: React.FC<SupportModeProviderProps> = ({ children }) => {
  // -------------------------------------------------------------------------
  // State Management
  // -------------------------------------------------------------------------

  const [supportTenant, setSupportTenant] = useState<SupportTenant | null>(() => {
    // Initialize from localStorage
    return readFromStorage();
  });

  // -------------------------------------------------------------------------
  // Computed Values
  // -------------------------------------------------------------------------

  /**
   * Derived state: whether admin is in support mode
   */
  const isInSupportMode = supportTenant !== null;

  // -------------------------------------------------------------------------
  // Persistence Effect
  // -------------------------------------------------------------------------

  /**
   * Persist support mode state to localStorage
   */
  useEffect(() => {
    writeToStorage(supportTenant);
  }, [supportTenant]);

  // -------------------------------------------------------------------------
  // Cross-Tab Synchronization
  // -------------------------------------------------------------------------

  /**
   * Listen for storage changes from other tabs
   * This ensures all open tabs stay in sync
   */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      // Only respond to changes to our storage key
      if (event.key !== SUPPORT_MODE_STORAGE_KEY) {
        return;
      }

      console.log('[SupportModeContext] Storage change detected from another tab');

      // Parse the new value
      if (event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue);
          setSupportTenant(parsed.supportTenant || null);
        } catch (err) {
          console.error('[SupportModeContext] Failed to parse storage event:', err);
        }
      } else {
        // Storage was cleared
        setSupportTenant(null);
      }
    };

    // Add listener for storage events
    window.addEventListener('storage', handleStorageChange);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Callbacks
  // -------------------------------------------------------------------------

  /**
   * Enter support mode for a specific tenant
   * Note: We write to localStorage synchronously BEFORE setting state
   * to ensure the data is available immediately for navigation
   */
  const enterSupportMode = useCallback((tenant: SupportTenant): void => {
    console.log('[SupportModeContext] Entering support mode for tenant:', tenant.name);
    // Write synchronously to ensure localStorage is updated before any navigation
    writeToStorage(tenant);
    setSupportTenant(tenant);
  }, []);

  /**
   * Exit support mode and clear state
   * Note: We write to localStorage synchronously BEFORE setting state
   */
  const exitSupportMode = useCallback((): void => {
    console.log('[SupportModeContext] Exiting support mode');
    // Write synchronously to ensure localStorage is cleared before any navigation
    writeToStorage(null);
    setSupportTenant(null);
  }, []);

  // -------------------------------------------------------------------------
  // Memoized Context Value
  // -------------------------------------------------------------------------

  /**
   * Memoize context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo<SupportModeContextType>(
    () => ({
      isInSupportMode,
      supportTenant,
      enterSupportMode,
      exitSupportMode,
    }),
    [isInSupportMode, supportTenant, enterSupportMode, exitSupportMode]
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <SupportModeContext.Provider value={contextValue}>
      {children}
    </SupportModeContext.Provider>
  );
};

// ============================================================================
// Consumer Hook
// ============================================================================

/**
 * Hook to access support mode context
 *
 * Must be used within a SupportModeProvider component.
 *
 * @returns Support mode context with state and methods
 * @throws Error if used outside of SupportModeProvider
 *
 * @example
 * const { isInSupportMode, supportTenant, exitSupportMode } = useSupportMode();
 *
 * if (isInSupportMode) {
 *   return <SupportModeBanner tenant={supportTenant} onExit={exitSupportMode} />;
 * }
 */
export const useSupportMode = (): SupportModeContextType => {
  const context = useContext(SupportModeContext);

  if (context === undefined) {
    throw new Error('useSupportMode must be used within a SupportModeProvider');
  }

  return context;
};

// ============================================================================
// Exports
// ============================================================================

export default SupportModeContext;
